/**
 * Campaign creation: a marketing manager orders a creator campaign. Lykke guides on the left (chat or a voice call);
 * the campaign takes shape on the right in the brand's own look. Only the ten must-haves are asked; the plan is the
 * media planner's engine, the ideas come from the Brand OS, and the order lands as a kickoff for the campaign team.
 * Replaces the old planner (beatly-planner-app) behind /campaigns/new.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { useEffectiveBrand } from '@/cowork/useActiveBrand';
import { deriveTheme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { RealtimeCall, type RealtimeTool } from '@/cowork/proposals/realtimeCall';
import { voiceErrorText } from '@/cowork/voice/voiceSession';
import { LykkeNavigator, type ThreadItem } from './LykkeNavigator';
import { OrderCanvas } from './OrderCanvas';
import { addWrote, applySet, audiencesFor, chapterReady, followerRange, initialState, missing, productLabel, stateForLykke, toOrderBody, type Chapter, type OrderState } from './orderState';
import type { OrderCreator, OrderIdea, OrderList, OrderPlan, OrderPrepare } from './types';

const OPENERS: Record<Chapter, (p: OrderPrepare, s: OrderState) => string> = {
  0: (p) => `I filled this in from your Brand OS${p.evidence.history?.last ? ` and ${p.evidence.history.last.name}` : ''}. Confirm the product, pick a goal and check the start date.`,
  1: (p) => `${p.audiences.length ? 'I picked the audiences linked to this product. ' : ''}Each idea below is built on what your Brand OS says. Pick one, or tell me what you have in mind.`,
  2: () => 'Point at a creator list to show the feel you want, or I scout a new one for this campaign. Your shortlist comes after the order and you choose from it. How do creators get the product?',
  3: () => 'Pick how you want to work with us and accept the terms. Then the campaign team sets it up and I keep you posted in Messages.',
};

export function CampaignCreation() {
  const { brandId, brandName } = useEffectiveBrand();
  const prepQ = useQuery({ queryKey: ['campaign-order', 'prepare', brandId], queryFn: () => coworkApi.campaignOrder.prepare(brandId!), enabled: !!brandId, staleTime: 5 * 60_000, retry: false });
  const graphQ = useQuery({ queryKey: ['cowork', 'brand-graph', brandId], queryFn: () => coworkApi.brands.get(brandId!), enabled: !!brandId, staleTime: 5 * 60_000 });
  const theme = useMemo(() => deriveTheme(graphQ.data || { brand: { name: brandName } }), [graphQ.data, brandName]);

  if (!brandId) return <Centered>Pick a brand in the top bar to order a campaign.</Centered>;
  if (prepQ.isLoading) return <Centered><Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />Reading your Brand OS</Centered>;
  if (prepQ.error || !prepQ.data) return <Centered>{(prepQ.error as Error)?.message || 'Campaign creation is not available right now.'}</Centered>;
  return <Flow key={brandId} prep={prepQ.data} theme={theme} />;
}


/** The canvas scrolls on its own on wide screens; the window on narrow ones. */
function toTop() {
  document.getElementById('order-canvas')?.scrollTo({ top: 0, behavior: 'smooth' });
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="grid min-h-[60vh] place-items-center px-4 text-center text-sm text-[#667085]"><p className="max-w-md">{children}</p></div>;
}

function Flow({ prep, theme }: { prep: OrderPrepare; theme: ReturnType<typeof deriveTheme> }) {
  const [state, setState] = useState<OrderState>(() => initialState(prep));
  const stateRef = useRef(state); stateRef.current = state;
  const [chapter, setChapter] = useState<Chapter>(0);
  const [maxChapter, setMaxChapter] = useState<Chapter>(0);
  const [thread, setThread] = useState<ThreadItem[]>(() => [{ kind: 'msg', who: 'lykke', text: OPENERS[0](prep, state) }]);
  const [busy, setBusy] = useState(false);

  const say = (text: string, who: 'lykke' | 'user' = 'lykke') => setThread((t) => [...t, { kind: 'msg', who, text }]);
  const wrote = (items: Array<{ field: string; value: string; chapter: Chapter }>) => items.length && setThread((t) => items.reduce((acc, w) => addWrote(acc, { kind: 'wrote', ...w }), t));

  const set = useCallback((patch: Partial<OrderState>, w?: { field: string; value: string }) => {
    setState((s) => ({ ...s, ...patch }));
    if (w) setThread((t) => addWrote(t, { kind: 'wrote', field: w.field, value: w.value, chapter: stateRefChapter.current }));
  }, []);
  const stateRefChapter = useRef<Chapter>(0); stateRefChapter.current = chapter;

  const pickProduct = (id: string) => {
    const pr = prep.products.find((x) => x.id === id);
    setState((s) => ({ ...s, productId: id, productName: '', audienceIds: audiencesFor(prep, id), idea: null }));
    if (pr) wrote([{ field: 'Product', value: pr.name, chapter: 0 }]);
  };

  const goTo = (ch: Chapter) => {
    if (ch > maxChapter && !(ch === maxChapter + 1 && chapterReady(stateRef.current, maxChapter))) return;
    setChapter(ch);
    if (ch > maxChapter) { setMaxChapter(ch); say(OPENERS[ch](prep, stateRef.current)); }
    toTop();
  };

  // ── The plan: the planner's engine, refreshed (debounced) when goal, budget, market or start change ──────────
  const [plan, setPlan] = useState<OrderPlan | null>(null);
  const planRef = useRef(plan); planRef.current = plan;
  const [planLoading, setPlanLoading] = useState(false);
  useEffect(() => {
    if (!state.goal || !(state.budget && state.budget > 0)) return;
    setPlanLoading(true);
    const t = setTimeout(() => {
      coworkApi.campaignOrder.plan({ brandId: prep.brand.id, brandName: prep.brand.name, goal: state.goal!, market: state.market, currency: state.currency, budget: state.budget!, start: state.start })
        .then(setPlan).catch(() => undefined).finally(() => setPlanLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [prep.brand.id, prep.brand.name, state.goal, state.budget, state.market, state.currency, state.start]);

  // ── Ideas: prefetched as soon as goal and product are known, refreshed when the audience changes ────────────
  const [ideas, setIdeas] = useState<OrderIdea[] | null>(null);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [ideasError, setIdeasError] = useState<string | null>(null);
  const audienceNames = useMemo(() => prep.audiences.filter((a) => state.audienceIds.includes(a.id)).map((a) => a.name), [prep.audiences, state.audienceIds]);
  const loadIdeas = useCallback((more = false) => {
    const s = stateRef.current; if (!s.goal) return;
    setIdeasLoading(true); setIdeasError(null);
    coworkApi.campaignOrder.ideas({ brandId: prep.brand.id, goal: s.goal, market: s.market, productName: productLabel(s, prep) || undefined, audiences: [...prep.audiences.filter((a) => s.audienceIds.includes(a.id)).map((a) => a.name), ...(s.audienceText.trim() ? [s.audienceText.trim()] : [])], more })
      .then((r) => setIdeas(r.ideas)).catch((e) => setIdeasError((e as Error).message)).finally(() => setIdeasLoading(false));
  }, [prep]);
  useEffect(() => {
    if (!state.goal) return;
    const t = setTimeout(() => loadIdeas(false), 600);
    return () => clearTimeout(t);
  }, [state.goal, state.productId, state.productName, state.market, audienceNames.join('|'), state.audienceText, loadIdeas]);

  // ── Creators who worked with this brand, with posts we already hold ────────────────────────────────────────
  const creatorsQ = useQuery({ queryKey: ['campaign-order', 'creators', prep.brand.id], queryFn: () => coworkApi.campaignOrder.creators(prep.brand.id), staleTime: 10 * 60_000 });
  const creators: OrderCreator[] | null = creatorsQ.data?.creators ?? (creatorsQ.isError ? [] : null);

  // ── Creator lists: real creators to point at; a new list is scouted on request ──────────────────────────────
  const listsQ = useQuery({ queryKey: ['campaign-order', 'lists', prep.brand.id, state.market], queryFn: () => coworkApi.campaignOrder.lists(prep.brand.id, state.market), staleTime: 10 * 60_000 });
  const [madeLists, setMadeLists] = useState<OrderList[]>([]);
  const lists: OrderList[] | null = listsQ.data || listsQ.isError ? [...madeLists, ...(listsQ.data?.lists || [])] : null;
  const [scouting, setScouting] = useState(false);
  const [scoutNote, setScoutNote] = useState<string | null>(null);
  const toggleList = (l: OrderList) => {
    const on = stateRef.current.lists.some((x) => x.id === l.id);
    set({ lists: on ? stateRef.current.lists.filter((x) => x.id !== l.id) : [...stateRef.current.lists, { id: l.id, title: l.title, scouted: l.scouted }] }, on ? undefined : { field: 'Creators like', value: l.title });
  };
  const scoutList = async (): Promise<OrderList | null> => {
    const s = stateRef.current; setScouting(true); setScoutNote(null);
    const product = prep.products.find((x) => x.id === s.productId);
    const productName = productLabel(s, prep);
    const range = followerRange(planRef.current?.sizeBand);
    try {
      const r = await coworkApi.campaignOrder.scoutList({
        brandId: prep.brand.id, market: s.market, idea: s.idea?.title, productName: productName || undefined, coverQuery: product?.category,
        title: `${prep.brand.name} · ${s.idea?.title || productName || 'campaign'} · ${prep.markets.find((m) => m.id === s.market)?.name || s.market}`,
        description: [s.idea?.description, [...prep.audiences.filter((a) => s.audienceIds.includes(a.id)).map((a) => a.name), s.audienceText.trim()].filter(Boolean).join(', '), productName].filter(Boolean).join('. '),
        minFollowers: range.min, maxFollowers: range.max,
      });
      const made = { ...r.list, scouted: true };
      setMadeLists((m) => [made, ...m]);
      setState((x) => ({ ...x, lists: [...x.lists, { id: made.id, title: made.title, scouted: true }] }));
      wrote([{ field: 'New creator list', value: `${made.title} (${made.count})`, chapter: 2 }]);
      if (r.note) setScoutNote(r.note);
      return made;
    } catch (e) { setScoutNote((e as Error).message); return null; } finally { setScouting(false); }
  };

  // ── Order ──────────────────────────────────────────────────────────────────────────────────────────────────
  const [ordering, setOrdering] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [result, setResult] = useState<{ name: string; plan: OrderPlan } | null>(null);
  const placeOrder = () => {
    const gaps = missing(stateRef.current);
    if (gaps.length) { setOrderError(`Still needed: ${gaps.map((g) => g.label.toLowerCase()).join(', ')}.`); return; }
    setOrdering(true); setOrderError(null);
    coworkApi.campaignOrder.order(toOrderBody(stateRef.current, prep))
      .then((r) => { setResult({ name: r.name, plan: r.plan }); say('Ordered. Your campaign team checks it and sets it up; I will message you here and in Messages when your creator shortlist is ready.'); toTop(); })
      .catch((e) => setOrderError((e as Error).message))
      .finally(() => setOrdering(false));
  };

  // ── Lykke by text ──────────────────────────────────────────────────────────────────────────────────────────
  const onSend = (text: string) => {
    say(text, 'user'); setBusy(true);
    const history = thread.filter((t): t is Extract<ThreadItem, { kind: 'msg' }> => t.kind === 'msg').slice(-8).map((t) => ({ role: t.who, text: t.text }));
    coworkApi.campaignOrder.converse({ brandId: prep.brand.id, message: text, state: stateForLykke(stateRef.current, prep), history })
      .then((r) => { const { next, wrote: w } = applySet(stateRef.current, r.set || {}, prep); setState(next); wrote(w); if (r.say) say(r.say); })
      .catch((e) => say((e as Error).message))
      .finally(() => setBusy(false));
  };

  // ── Lykke by voice: tools act on the same order ────────────────────────────────────────────────────────────
  const [call, setCall] = useState<'off' | 'connecting' | 'live'>('off');
  const [caption, setCaption] = useState('');
  const callRef = useRef<RealtimeCall | null>(null);
  const goToRef = useRef(goTo); goToRef.current = goTo;
  useEffect(() => () => callRef.current?.end(), []);
  const tools: RealtimeTool[] = useMemo(() => [
    {
      name: 'set_order', description: 'Write values the user just said into the order. Only values they stated.',
      parameters: { type: 'object', properties: { product: { type: 'string', description: 'What the campaign promotes: a Brand OS product name, or the user\'s own words when none fits' }, audience: { type: 'string', description: 'Who to reach: a Brand OS audience name, or the user\'s own words when none fits' }, goal: { type: 'string', enum: prep.goals.map((g) => g.id) }, market: { type: 'string', enum: prep.markets.map((m) => m.id) }, budget: { type: 'number' }, start: { type: 'string', description: 'YYYY-MM-DD' }, delivery: { type: 'string', enum: prep.delivery.map((d) => d.id) }, approverEmail: { type: 'string' }, note: { type: 'string' } } },
      execute: async (args) => { const { next, wrote: w } = applySet(stateRef.current, args, prep); setState(next); wrote(w); return { saved: w.map((x) => x.field), missing: missing(next).map((m) => m.label) }; },
    },
    {
      name: 'choose', description: 'Choose a product, an audience, an idea or the service by its name as shown on screen.',
      parameters: { type: 'object', properties: { what: { type: 'string', enum: ['product', 'audience', 'idea', 'service', 'list'] }, name: { type: 'string' } }, required: ['what', 'name'] },
      execute: async (args) => {
        const name = String(args.name || '').toLowerCase(); const what = String(args.what);
        const match = <T extends { name?: string; title?: string }>(xs: T[]) => xs.find((x) => (x.name || x.title || '').toLowerCase().includes(name) || name.includes((x.name || x.title || '').toLowerCase()));
        if (what === 'product') { const pr = match(prep.products); if (pr) { pickProduct(pr.id); return { ok: true, chosen: pr.name }; } }
        if (what === 'audience') { const a = match(prep.audiences); if (a) { set({ audienceIds: [...new Set([...stateRef.current.audienceIds, a.id])] }, { field: 'Audience', value: a.name }); return { ok: true, chosen: a.name }; } }
        if (what === 'idea') { const i = match(ideasRef.current || []); if (i) { set({ idea: i }, { field: 'Idea', value: i.title }); return { ok: true, chosen: i.title }; } }
        if (what === 'list') { const l = (listsRef.current || []).find((x) => x.title.toLowerCase().includes(name) || name.includes(x.title.toLowerCase())); if (l) { if (!stateRef.current.lists.some((x) => x.id === l.id)) toggleListRef.current(l); return { ok: true, chosen: l.title }; } }
        if (what === 'service') { const svc = /auto/.test(name) ? 'autonomous' : 'full'; set({ service: svc }, { field: 'Service', value: svc === 'full' ? 'Full service' : 'Autonomous' }); return { ok: true, chosen: svc }; }
        return { ok: false, error: `No ${what} called "${args.name}" on screen.` };
      },
    },
    {
      name: 'show_chapter', description: 'Move the screen to a chapter: 1 Brand and goal, 2 Audience and idea, 3 Creators and plan, 4 Order.', silent: true,
      parameters: { type: 'object', properties: { chapter: { type: 'number', enum: [1, 2, 3, 4] } }, required: ['chapter'] },
      execute: async (args) => { const ch = (Math.max(1, Math.min(4, Number(args.chapter))) - 1) as Chapter; goToRef.current(ch); return { ok: true }; },
    },
    {
      name: 'scout_list', description: 'Make a new creator list for this campaign now and fill it with a first search. Use when no list on screen fits or the user asks for new creators.',
      parameters: { type: 'object', properties: {} },
      execute: async () => { const l = await scoutListRef.current(); return l ? { ok: true, title: l.title, creators: l.count } : { ok: false } ; },
    },
    { name: 'get_order', description: 'Read the order as it is now, with what is still missing.', parameters: { type: 'object', properties: {} }, execute: async () => ({ order: stateForLykke(stateRef.current, prep), ideas: (ideasRef.current || []).map((i) => i.title), creatorLists: (listsRef.current || []).map((l) => l.title) }) },
  ], [prep]); // tools read live state through refs
  const ideasRef = useRef(ideas); ideasRef.current = ideas;
  const listsRef = useRef(lists); listsRef.current = lists;
  const toggleListRef = useRef(toggleList); toggleListRef.current = toggleList;
  const scoutListRef = useRef(scoutList); scoutListRef.current = scoutList;

  const onCall = async () => {
    if (call !== 'off') { callRef.current?.end(); callRef.current = null; setCall('off'); setCaption(''); say('Call ended. Everything we agreed is written in the order.'); return; }
    setCall('connecting');
    try {
      const rc = new RealtimeCall(tools, {
        onStatus: (st) => { if (st === 'connected') setCall('live'); if (st === 'ended' || st === 'error') { setCall('off'); setCaption(''); } },
        onTranscript: (role, text, final) => { if (final && text.trim()) say(text.trim(), role === 'kai' ? 'lykke' : 'user'); else setCaption(text); },
        onError: (m) => say(m),
        // Her minutes for this call ran out: a calm line in the thread, the call ends.
        onTimeUp: (m) => say(`${m} You can keep going in the chat.`),
      });
      callRef.current = rc;
      // The offer goes to the mint route: the server connects the call and keeps its voice session (voiceSession.ts).
      await rc.connect((sdp) => coworkApi.campaignOrder.realtimeSecret({ brandId: prep.brand.id, state: stateForLykke(stateRef.current, prep), sdp }), undefined, (s) => ({ instructions: s.instructions, voice: s.voice, opening: `Open with one short line: what is already filled in from their Brand OS, then ask the first missing thing (${missing(stateRef.current)[0]?.label || 'anything to change'}).` }));
    } catch (e) {
      callRef.current = null;
      setCall('off'); say(`${voiceErrorText(e, 'Voice is unavailable right now.')} You can keep going in the chat.`);
    }
  };

  return (
    <div className="flex flex-col lg:h-[calc(100vh-3.5rem)] lg:flex-row lg:overflow-hidden">
      <LykkeNavigator state={state} thread={thread} busy={busy} call={call} callCaption={caption} onSend={onSend} onCall={onCall} onJump={goTo} />
      <OrderCanvas
        prep={prep} theme={theme} state={state} chapter={chapter} maxChapter={maxChapter} set={set} pickProduct={pickProduct}
        plan={plan} planLoading={planLoading} ideas={ideas} ideasLoading={ideasLoading} ideasError={ideasError} onMoreIdeas={() => loadIdeas(true)}
        creators={creators} creatorsNote={creatorsQ.data?.note ?? null}
        lists={lists} listsNote={listsQ.data?.note ?? null} scouting={scouting} scoutNote={scoutNote} onToggleList={toggleList} onScout={() => { void scoutList(); }}
        onChapter={goTo} onOrder={placeOrder} ordering={ordering} orderError={orderError} result={result}
      />
    </div>
  );
}
